import { useState } from 'react'
import { LoaderCircle } from 'lucide-react'
import { Button as ButtonComponent } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import HelperDialog from '@/components/campaigns/HelperDialog'
import {
    useRefineMedia,
    GeneratedAsset,
    RefineMediaType,
    RefineTurn,
} from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/useRefineMedia'
import { ButtonProps } from '@/types'

// Type cast for the Button component from JSX file
const Button = ButtonComponent as React.FC<
    ButtonProps & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'aria-label'>
>

const FEEDBACK_LIMIT = 1000
export const PROMPT_LIMIT = 3000
const VIDEO_NOTE = 'Each regeneration takes about 3 to 4 minutes and counts as a new video generation.'
const EDITED_PROMPT_LABEL = 'Edited the prompt'

interface RefineMediaDialogProps {
    mediaType: RefineMediaType
    asset: GeneratedAsset
    campaignId?: string | null
    open: boolean
    /** Called with the record still generating when the dialog closes mid-turn, else null */
    onClose: (pending: GeneratedAsset | null) => void
    /** Called once with the regenerated asset that completed: it replaces the source and the dialog is done */
    onReplaced?: (asset: GeneratedAsset) => void
}

const mediaUrl = (url: string | null) => `${import.meta.env.VITE_S3_BUCKET_URL}${url ?? ''}`

const AssetPreview = ({ mediaType, asset }: { mediaType: RefineMediaType; asset: GeneratedAsset }) =>
    mediaType === 'VIDEO' ? (
        <video
            src={mediaUrl(asset.url)}
            className="tw:max-h-64 tw:rounded-xs tw:bg-black"
            muted
            loop
            playsInline
            autoPlay
        />
    ) : (
        <img
            src={mediaUrl(asset.url)}
            alt={asset.name || 'Generated AI image'}
            className="tw:max-h-64 tw:rounded-xs tw:object-contain"
        />
    )

const TurnList = ({ turns }: { turns: RefineTurn[] }) =>
    turns.length === 0 ? null : (
        <ul aria-label="Refinement turns" className="tw:flex tw:flex-col tw:gap-3 tw:m-0 tw:p-0 tw:list-none">
            {turns.map((turn, index) => (
                <li key={index} className="tw:flex tw:flex-col tw:gap-2">
                    <div className="tw:rounded-md tw:bg-[#334155] tw:px-3 tw:py-2 tw:self-end tw:max-w-[85%]">
                        <span className="tw:block tw:text-xs tw:text-[#94A3B8]">You</span>
                        <span className="tw:block tw:text-sm tw:text-foreground tw:whitespace-pre-wrap">
                            {turn.feedback || EDITED_PROMPT_LABEL}
                        </span>
                    </div>
                    <div className="tw:rounded-md tw:border tw:border-gray-600 tw:px-3 tw:py-2 tw:max-w-[85%]">
                        <span className="tw:block tw:text-xs tw:text-gray-400">
                            {turn.feedback ? 'Revised prompt' : 'Prompt'}
                        </span>
                        <span className="tw:block tw:text-sm tw:text-gray-300 tw:whitespace-pre-wrap">
                            {turn.prompt}
                        </span>
                    </div>
                </li>
            ))}
        </ul>
    )

/**
 * The dialog a user refines a generated image or video in: the current asset, its prompt to edit on demand, the
 * turns of the thread so far, and a box for what should change. Regenerate sends the feedback, the edited prompt,
 * or both, and regenerates the asset in its own format; when the new one completes it takes the source's place
 * (`onReplaced`) and the dialog is done. The thread lives only while the dialog is open.
 */
const RefineMediaDialog = ({ mediaType, asset, campaignId, open, onClose, onReplaced }: RefineMediaDialogProps) => {
    const { current, turns, status, error, pending, send } = useRefineMedia({
        mediaType,
        asset,
        campaignId,
        onGenerated: onReplaced,
    })
    const [feedback, setFeedback] = useState('')
    const [promptShown, setPromptShown] = useState(false)
    const [promptDraft, setPromptDraft] = useState(current.prompt ?? '')
    const busy = status === 'sending' || status === 'processing'
    const promptChanged = promptDraft.trim() !== (current.prompt ?? '').trim()
    const canSend = !busy && (feedback.trim().length > 0 || (promptChanged && promptDraft.trim().length > 0))
    const label = mediaType === 'VIDEO' ? 'video' : 'image'

    const handleSend = () => {
        if (!canSend) return
        const text = feedback.trim()
        setFeedback('')
        void send({
            ...(text ? { feedback: text } : {}),
            ...(promptChanged ? { prompt: promptDraft.trim() } : {}),
        })
    }

    return (
        <HelperDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose(pending)} title={`Refine this ${label}`}>
            <div className="tw:flex tw:flex-col tw:gap-4">
                <div className="tw:flex tw:flex-col tw:items-start tw:gap-2">
                    <AssetPreview mediaType={mediaType} asset={current} />
                    <Button
                        type="button"
                        variant="link"
                        className="tw:px-0 tw:h-auto tw:text-sm"
                        onClick={() => setPromptShown((shown) => !shown)}
                    >
                        {promptShown ? 'Hide prompt' : 'Edit prompt'}
                    </Button>
                    {promptShown && (
                        <div className="tw:flex tw:flex-col tw:gap-2 tw:self-stretch">
                            <label htmlFor="refine-prompt" className="tw:text-sm tw:font-semibold">
                                Prompt
                            </label>
                            <Textarea
                                id="refine-prompt"
                                value={promptDraft}
                                maxLength={PROMPT_LIMIT}
                                disabled={busy}
                                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                                    setPromptDraft(event.target.value)
                                }
                                className="tw:min-h-32 tw:pr-3"
                            />
                            <div className="tw:flex tw:items-center tw:justify-between tw:text-xs tw:text-gray-400">
                                <span>
                                    {promptChanged
                                        ? 'Regenerate uses this prompt; add feedback and it is revised from it.'
                                        : 'Change the prompt to regenerate from it.'}
                                </span>
                                <span>
                                    {promptDraft.length} / {PROMPT_LIMIT}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <TurnList turns={turns} />

                {busy && (
                    <p role="status" className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-gray-300 tw:m-0">
                        <LoaderCircle size={16} className="tw:animate-spin" />
                        Generating…
                    </p>
                )}
                {status === 'failed' && error && (
                    <p role="alert" className="tw:text-sm tw:text-red-400 tw:m-0">
                        {error}
                    </p>
                )}

                {mediaType === 'VIDEO' && <p className="tw:m-0 tw:text-sm tw:text-gray-400">{VIDEO_NOTE}</p>}

                <div className="tw:flex tw:flex-col tw:gap-2">
                    <label htmlFor="refine-feedback" className="tw:text-sm tw:font-semibold">
                        What should change?
                    </label>
                    <Textarea
                        id="refine-feedback"
                        value={feedback}
                        maxLength={FEEDBACK_LIMIT}
                        disabled={busy}
                        placeholder="Describe what to change, for example the colours, the setting or the mood"
                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setFeedback(event.target.value)}
                        className="tw:min-h-20 tw:pr-3"
                    />
                    <div className="tw:flex tw:items-center tw:justify-between">
                        <span className="tw:text-xs tw:text-gray-400">
                            {feedback.length} / {FEEDBACK_LIMIT}
                        </span>
                        <Button type="button" variant="secondary" onClick={handleSend} disabled={!canSend}>
                            Regenerate
                        </Button>
                    </div>
                </div>
            </div>
        </HelperDialog>
    )
}

export default RefineMediaDialog
